import {TableEditableProps} from 'notebook/tables/core-ui/TableEditableProps';
import {TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import {
  createTableContextSelector,
  TableContextModel,
} from 'notebook/tables/store/selectors/tableModel/TableContextSelectors';
import {
  createTableDimensionsSelector,
  TableDimensionsModel,
} from 'notebook/tables/store/selectors/tableModel/TableDimensionsSelectors';
import {
  createTableLockingSelector,
  TableLockingModel,
} from 'notebook/tables/store/selectors/tableModel/TableLockingSelectors';
import {
  createTableSelectionSelector,
  TableSelectionModel,
} from 'notebook/tables/store/selectors/tableModel/TableSelectionSelectors';
import {
  createBoundaryIndexSelector,
  createTableSingleInputsSelector,
  TableSingleInputsModel,
} from 'notebook/tables/store/selectors/tableModel/TableSingleInputsSelectors';
import {
  createTableViewsSelector,
  TableViewsModel,
} from 'notebook/tables/store/selectors/tableModel/TableViewsSelectors';
import {
  createDebuggableSelector,
  createDeepSelector,
} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableVolatileProps, TableEditableStore} from 'notebook/tables/store/TableStore';

/**
 * This model is what is passed down to the components, as well as to the RowModel.
 * This can contain data that is derived from the TableEditableStore.
 *
 * Ideally, logic from TableEditable should move into selectors and those selectors should generate values
 * that are stored on this object.
 */
export interface TableModel {
  props: TableEditableProps;
  state: TableEditableState;
  selection: TableSelectionModel;
  context: TableContextModel;
  dimensions: TableDimensionsModel;
  singleInputs: TableSingleInputsModel;
  locking: TableLockingModel;
  views: TableViewsModel;

  /*
  TODO(BNCH-146929): Future models to be added as logic is transitioned out of TableEditable:

  // Active cell formatting for formula bar display
  activeCell: {
    precision: number | null; // From getSelectionPrecision() for single cell
    alignment: AlignmentState; // From getSelectionAlignment() for single cell
    link: {url?: string | null; text?: string} | null; // From getSelectionLink()
    isDatetimeCell: boolean; // From _isSelectionOnlyDatetimeCells() for single cell
    linkButtonDisabled: boolean; // From linkButtonDisabled()
    buttonFormatting: {
      active: boolean; // Currently merged
      disabled: boolean; // Can't merge
    }; // From _mergeButtonFormatting()
  };

  measurements: {
    currentTableHeight: number; // From getCurrentTableHeight()
    maxTableHeight: number; // From getMaxTableHeight()
    minTableHeight: number; // From getMinTableHeight()
    titleBarHeight: number; // From getTitleBarHeight()
    stickyTopContentHeight: number; // From getStickyTopContentHeight()
    formulaBarHeight: number; // From getFormulaBoxHeight()
    indentationPx: number; // From _indentationPx()
    paddingPx: number; // From _paddingPx()
  };

  visibility: {
    showFooter: boolean; // From shouldRenderFooter()
    showFormulaBar: boolean; // From isFormulaBoxVisible()
    isVirtualizationEnabled: boolean; // From isVirtualizationEnabledForTable()
    isStickyHeaderVisible: boolean; // From _isStickyHeaderVisible()
  };

  merging: {
    isEnabled: boolean; // From !disableMergingCells prop
    supportsMerge: boolean; // From supportsMerge()
  };

  fillDown: {
    isEnabled: boolean; // From enableFillDown prop
    isActive: boolean; // From isFillDownActive()
    isInvalid: boolean; // From isFillDownInvalid()
    isLocked: boolean; // From _isFillDownBoxEditingLocked()
    box: MultiSelectionBox | null | undefined; // From getFillDownBox()
    renderBox: MultiSelectionBox | null; // From getFillDownRenderBox()
    doubleClickBox: MultiSelectionBox | null; // From getDoubleClickFillDownBox()
  };

  reordering: {
    shouldShowDragHandles: {
      row: boolean; // From _shouldShowDragHandles(ROW)
      column: boolean; // From _shouldShowDragHandles(COLUMN)
    };
    draggingState: {
      dimension: TableDimension | null;
      range: SelectionRange | null;
    }; // From getDraggingRange()
  };
  */
}

export function createVolatileTablePropsSelector(): (
  store: TableEditableStore
) => TableEditableVolatileProps {
  return createDeepSelector.withTypes<TableEditableStore>()(
    [(store) => store.volatileProps],
    (volatileProps): TableEditableVolatileProps => ({...volatileProps})
  );
}

export function createTableModelSelector(): (store: TableEditableStore) => TableModel {
  const volatileSelector = createVolatileTablePropsSelector();
  const tableContextSelector = createTableContextSelector(volatileSelector);
  const boundaryIndexSelector = createBoundaryIndexSelector(volatileSelector);
  const tableSingleInputsSelector = createTableSingleInputsSelector(volatileSelector);
  const tableSelectionSelector = createTableSelectionSelector(volatileSelector, boundaryIndexSelector);
  const tableViewsSelector = createTableViewsSelector(volatileSelector, tableContextSelector);
  const tableDimensionsSelector = createTableDimensionsSelector(
    volatileSelector,
    tableContextSelector,
    tableSelectionSelector,
    tableViewsSelector
  );
  const tableLockingSelector = createTableLockingSelector(volatileSelector, tableSelectionSelector);

  return createDebuggableSelector.withTypes<TableEditableStore>()(
    [
      volatileSelector,
      (store) => store.stableProps,
      (store) => store.state,
      tableSelectionSelector,
      tableContextSelector,
      tableDimensionsSelector,
      tableSingleInputsSelector,
      tableLockingSelector,
      tableViewsSelector,
    ],
    (
      volatileProps,
      props,
      state,
      selection,
      context,
      dimensions,
      singleInputs,
      locking,
      views
    ): TableModel => ({
      props: {
        ...props,
        ...volatileProps,
      },
      state,
      selection,
      context,
      dimensions,
      singleInputs,
      locking,
      views,
    })
  );
}
